import classNames from 'classnames';
import { appRoutes } from 'config';
import { reportUserClickedOnEvent } from 'utils/analytics/segment';
import Button from 'elements/Button';
import CountryMenu from 'elements/CountryMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSunBright } from '@fortawesome/pro-regular-svg-icons';
import { NavMenu } from './NavMenu';
import { MobileMenu } from './MobileMenu';
import { Banner } from 'modules/Landing/components/Banner';
import { useBanner } from 'modules/Landing/hooks';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from 'core/i18n/navigation';
import useWindowScroll from 'hooks/useWindowScroll';
import { useMarketingTheme } from 'hooks/useMarketingTheme';
import { useLoginModalTrigger } from 'hooks/useLoginModalTrigger';
import Logotype from 'assets/icons/logotype.svg';

// `appearance` is the look at the top of the page; `scrolledAppearance` is what
// a transparent header turns into once scrolled. The resolved nav chrome is one
// of three closed values, so illegal combinations can't be represented.
export type HeaderAppearance = 'solid' | 'transparent';
export type HeaderScrolledAppearance = 'solid' | 'frosted';
type HeaderChrome = 'solid' | 'transparent' | 'frosted';

// Past this scroll offset a transparent header leaves its at-top look.
const SCROLL_THRESHOLD = 50;

type Props = {
  /** Look at the top of the page. */
  appearance?: HeaderAppearance;
  /**
   * What a transparent header becomes once scrolled (ignored for `solid`):
   * `solid` = opaque white, `frosted` = dark translucent + blur.
   */
  scrolledAppearance?: HeaderScrolledAppearance;
  /**
   * Page follows the visitor's marketing theme: the chrome tints via
   * `marketing-dark:` utilities (light by default, today's dark look under
   * `.marketing-dark`) and a subtle theme toggle appears in the actions.
   */
  themed?: boolean;
};

// Logo and auth buttons only care whether the chrome sits on light or dark;
// `themed` defers the choice to CSS (light classes + `marketing-dark:` overrides).
type Tone = 'light' | 'dark' | 'themed';
type LinkProps = { href: string; tone: Tone };

const LoginLink = ({ href, tone }: LinkProps) => {
  const t = useTranslations();
  const openLoginModal = useLoginModalTrigger(href);
  return (
    <Link
      href={href}
      className="hidden xs:block"
      onClick={(event) => {
        reportUserClickedOnEvent('Get Started', 'Homepage Header');
        openLoginModal(event);
      }}
    >
      <Button
        type="button"
        variant="outline"
        color="neutral"
        size="sm"
        className={classNames(styles.btn.lift, LOGIN_BUTTON[tone])}
      >
        {t('auth.logIn')}
      </Button>
    </Link>
  );
};

const StartNowLink = ({ href, tone }: LinkProps) => {
  const t = useTranslations();
  const openLoginModal = useLoginModalTrigger(href);
  return (
    <Link
      href={href}
      onClick={(event) => {
        reportUserClickedOnEvent('Get Started', 'Homepage Header');
        openLoginModal(event);
      }}
    >
      <Button
        type="button"
        variant="solid"
        color="primary"
        size="sm"
        className={classNames(
          'bg-mf-blue hover:bg-mf-blue/90',
          styles.btn.lift,
          START_NOW_BUTTON[tone],
        )}
      >
        {/* Phones show the lone CTA as "Get Started"; xs+ shows "Sign up free"
            beside the Log in button. CSS picks the copy so SSR stays exact. */}
        <span className="xs:hidden">{t('auth.getStarted')}</span>
        <span className="hidden xs:inline">{t('auth.signUpFree')}</span>
      </Button>
    </Link>
  );
};

// Quick switch: the icon shows what's currently showing (sun = light,
// moon = dark) and a tap flips it. The cookie expires back to the OS scheme
// after 24h (see hooks/useMarketingTheme). Both icons are rendered and CSS
// picks one via `marketing-dark:` — driven by the same <html> class the
// pre-paint script sets, so the right icon shows from the first frame
// (JS-chosen icons would flash the SSR default until hydration).
const ThemeToggle = () => {
  const t = useTranslations();
  const { resolvedTheme, toggleTheme } = useMarketingTheme();
  const label = t(`nav.theme.${resolvedTheme}`);
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      className={styles.themeToggle}
    >
      <span className="flex marketing-dark:hidden">
        <FontAwesomeIcon icon={faSunBright} className="h-4 w-4" />
      </span>
      <span className="hidden marketing-dark:flex">
        <FontAwesomeIcon icon={faMoon} className="h-4 w-4" />
      </span>
    </button>
  );
};

const VisitorHeader = ({
  appearance = 'solid',
  scrolledAppearance = 'solid',
  themed = false,
}: Props) => {
  // next-intl usePathname is locale-agnostic (`/login`, `/blog/x`).
  const pathname = usePathname();
  // Hydration-safe: reports 0 during the first client render (matching the
  // SSR markup even when the browser restored a scroll position), then the
  // real offset right after — see useWindowScroll.
  const { y } = useWindowScroll();
  // Pages that hide the nav (mega-menu, mobile drawer and auth CTAs).
  const hideMenu = [
    appRoutes.LOGIN,
    appRoutes.PRIVACY_POLICY,
    appRoutes.TERMS_OF_USE,
    appRoutes.HELP_CENTRE,
  ].includes(pathname);
  const { variant: bannerVariant, showBanner, dismissBanner } = useBanner();

  // A solid header is always solid. A transparent header is clear at the top,
  // then becomes its `scrolledAppearance` (white or dark/frosted) once scrolled.
  const chrome: HeaderChrome =
    appearance !== 'transparent'
      ? 'solid'
      : y > SCROLL_THRESHOLD
        ? scrolledAppearance
        : 'transparent';
  const onDark = chrome !== 'solid';
  // Themed pages defer light/dark to CSS; the solid chrome stays light either
  // way (an opaque white bar), matching the non-themed behaviour.
  const tone: Tone = themed && onDark ? 'themed' : onDark ? 'dark' : 'light';
  const navClass = (themed ? THEMED_NAV_CHROME : NAV_CHROME)[chrome];

  return (
    <>
      {showBanner && (
        <Banner variant={bannerVariant} onDismiss={dismissBanner} />
      )}
      <nav
        className={classNames(
          styles.nav,
          showBanner ? 'top-9' : 'top-0',
          navClass,
        )}
      >
        {/* 3-zone grid: logo (left), nav mega-menus (centre), actions (right). */}
        <div className={styles.container}>
          {/* Logo — one `currentColor` SVG whose tint transitions between dark
            (light chrome) and white (dark/transparent chrome). */}
          <div className={styles.logo.container}>
            <Link href={appRoutes.HOME} className={styles.logo.href}>
              <span
                data-testid="visitor-logo"
                className={classNames(styles.logo.tint, LOGO_TINT[tone])}
              >
                <Logotype
                  className={styles.logo.svg}
                  role="img"
                  aria-label="MindFeed"
                />
              </span>
            </Link>
          </div>

          {/* Hide the mega-menu on login/legal/help pages — keep just the logo. */}
          {!hideMenu ? (
            <NavMenu
              appearance={themed ? 'themed' : appearance}
              className="justify-self-center"
            />
          ) : (
            <div />
          )}
          <div className="lg:hidden" />

          <div className={styles.right}>
            {!hideMenu && <LoginLink href={appRoutes.LOGIN} tone={tone} />}
            {!hideMenu && <StartNowLink href={appRoutes.LOGIN} tone={tone} />}
            {/* We use <CountryMenu /> but we could also use <LocaleMenu /> */}
            <div className={styles.country}>
              <CountryMenu />
            </div>
            {themed && <ThemeToggle />}
            {!hideMenu && <MobileMenu onDark={onDark} themed={themed} />}
          </div>
        </div>
      </nav>
    </>
  );
};

export default VisitorHeader;

// Each resolved chrome's nav styling in one place — adding a new look is a
// single entry here, not scattered ternaries. `frosted` mirrors the dark,
// translucent, blurred bar from the v3 design.
const NAV_CHROME: Record<HeaderChrome, string> = {
  solid: 'bg-white border-b border-gray-200 shadow-xs',
  transparent: 'bg-transparent border-b border-transparent',
  frosted:
    'bg-mf-navy/85 backdrop-blur-md border-b border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.3)]',
};

// Marketing-theme-aware chrome: the light look by default (white frosted bar per
// the light design) and exactly NAV_CHROME's dark look under `.marketing-dark`.
const THEMED_NAV_CHROME: Record<HeaderChrome, string> = {
  solid: NAV_CHROME.solid,
  transparent: NAV_CHROME.transparent,
  frosted: classNames(
    'bg-white/90 backdrop-blur-md border-b border-[#F0F0F0] shadow-none',
    'marketing-dark:bg-mf-navy/85 marketing-dark:border-white/10',
    'marketing-dark:shadow-[0_10px_30px_rgba(0,0,0,0.3)]',
  ),
};

// Auth buttons follow the chrome's tone so they stay legible: light treatment
// on a white header, light-on-dark treatment over a transparent/frosted header.
// `themed` composes both, switched by the marketing theme.
const LOGIN_BUTTON: Record<Tone, string> = {
  light: 'border border-mf-gray-300 text-mf-off-black-950',
  dark: 'border border-white/40 bg-transparent text-white hover:border-white/70 hover:bg-white/10',
  themed: classNames(
    'border border-mf-gray-300 text-mf-off-black-950',
    'marketing-dark:border-white/40 marketing-dark:bg-transparent marketing-dark:text-white',
    'marketing-dark:hover:border-white/70 marketing-dark:hover:bg-white/10',
  ),
};

const START_NOW_BUTTON: Record<Tone, string> = {
  light: '',
  dark: 'shadow-[0_8px_22px_rgba(39,87,186,0.4)]',
  themed:
    'shadow-[0_2px_8px_rgba(39,87,186,0.22)] marketing-dark:shadow-[0_8px_22px_rgba(39,87,186,0.4)]',
};

const LOGO_TINT: Record<Tone, string> = {
  light: 'text-[#0E1738]',
  dark: 'text-white',
  themed: 'text-[#0E1738] marketing-dark:text-white',
};

const styles = {
  // `top` is applied by the component (drops below the announcement bar when shown).
  nav: 'fixed z-20 w-full transition-[background-color,border-color,box-shadow] duration-500 ease-out',
  container:
    'mx-auto grid h-16 w-full max-w-8xl grid-cols-[1fr_auto_1fr] items-center gap-4 px-5',
  btn: {
    lift: 'hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0',
  },
  logo: {
    container: 'flex justify-self-start',
    href: 'flex items-center',
    // The tint transitions on scroll so the light/dark swap eases instead of
    // popping; the SVG inherits it via `currentColor`.
    tint: 'inline-flex transition-colors duration-500 ease-out',
    svg: 'block h-4 w-auto',
  },
  right: 'flex items-center gap-2 justify-self-end',
  country: 'hidden items-center lg:flex',
  // Quiet icon button switching the marketing theme. Desktop-only (lg+),
  // like the country switcher beside it. Pressed (`active:`) holds the hover
  // look; keep in sync with the CountryMenu trigger, which mirrors this design.
  themeToggle: classNames(
    'hidden lg:flex h-9 w-9 items-center justify-center rounded-full transition-colors',
    'text-mf-gray-500 hover:bg-black/5 hover:text-mf-off-black-950',
    'active:bg-black/5 active:text-mf-off-black-950',
    'marketing-dark:text-white/60 marketing-dark:hover:bg-white/10 marketing-dark:hover:text-white',
    'marketing-dark:active:bg-white/10 marketing-dark:active:text-white',
  ),
};
